import type { FC, ReactElement } from 'react';

import useRole from '@zen/Auth/hooks/useRole';
import useGlobalPermissions from '@zen/Auth/useGlobalPermissions';
import Page from '@zen/Components/Page';
import QueryHandler from '@zen/Components/QueryHandler';
import type { RouteTabConfig } from '@zen/Components/RouteTabs';
import RouteTabs from '@zen/Components/RouteTabs';
import RouteTabSwitcher from '@zen/Components/RouteTabSwitcher';
import { useFeatureIsOn } from '@zen/FeatureFlag';
import { Role } from '@zen/Networks/types';
import { accountsRoutes } from '@zen/Routes';
import useAccount from '@zen/utils/hooks/useAccount';

import { useGetAccountDetailsQuery } from '../AccountDetails/graphql';
import type { Account } from '../AccountDetails/types';
import { getTabsConfig } from './helpers';

const AccountSettings: FC = () => {
  const { userProfile } = useAccount();
  const { check } = useGlobalPermissions();
  const canViewProductSettings: boolean = useFeatureIsOn('new-products');
  const isSopsEnabled = useFeatureIsOn('sops');

  const accountId: string = userProfile.accountUuid;

  const role = useRole();
  const canViewAccountGeneral: boolean = check('accounts.canViewAccountGeneral');
  const canViewAuthentication: boolean = check('accounts.canViewAuthentication');
  const canViewBusinessUnits: boolean = check('accounts.canViewBusinessUnits');
  const canViewLocations: boolean = check('accounts.canViewLocations');
  const canViewMembers: boolean = check('accounts.canViewMembers');

  const defaultBackUrl = role === Role.ADMIN ? accountsRoutes.accountsIndex.getUrl() : undefined;

  const { data, error, loading } = useGetAccountDetailsQuery({
    variables: { accountId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'network-only'
  });

  return (
    <QueryHandler data={data?.account} error={!!error} isLoading={loading}>
      {(accountDetails: Account): ReactElement => {
        const { referencePrefix, tradingName, permissions } = accountDetails;

        const canViewConnections: boolean = !!permissions?.settings?.canViewConnections?.value;
        const canViewWebhooks: boolean = !!permissions?.settings?.canViewWebhooks?.value;
        const canViewSOPs: boolean = !!permissions?.settings?.canViewSOPs?.value && isSopsEnabled;

        const tabsConfig: RouteTabConfig[] = getTabsConfig(accountDetails, {
          canViewAccountGeneral,
          canViewAuthentication,
          canViewBusinessUnits,
          canViewConnections,
          canViewLocations,
          canViewMembers,
          canViewProductSettings,
          canViewSOPs,
          canViewWebhooks
        });

        return (
          <Page
            defaultBackUrl={defaultBackUrl}
            tabsComponent={<RouteTabs preserveSearchParams={false} tabsConfig={tabsConfig} />}
            tagline={referencePrefix}
            title={tradingName}
          >
            <RouteTabSwitcher tabsConfig={tabsConfig} />
          </Page>
        );
      }}
    </QueryHandler>
  );
};

export default AccountSettings;
